"use client";

import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import Map, { NavigationControl } from "react-map-gl/mapbox";
import type { MapRef } from "react-map-gl/mapbox";
import "mapbox-gl/dist/mapbox-gl.css";
import { ILocation, type MapBounds } from "@/services/locationApi";
import { LocationCluster, buildLocationClusters } from "@/lib/stateClusters";
import { ClusterMarker, LocationMarker, LocationPopup } from "./map-markers";

const WIDE_CLUSTER_ZOOM = 4; // below → wide clusters (350mi radius)
const MEDIUM_CLUSTER_ZOOM = 6; // below → medium clusters (40mi radius), at/above → individual markers

export const INDIVIDUAL_MARKER_ZOOM = MEDIUM_CLUSTER_ZOOM;

export type MapCenterChange = {
  lat: number;
  lng: number;
  zoom: number;
  bounds: MapBounds;
};

type MapViewStateEvent = {
  viewState: { latitude: number; longitude: number; zoom: number };
  originalEvent?: Event;
};

function isUserMapGesture(event: MapViewStateEvent): boolean {
  return event.originalEvent != null;
}

export interface MapComponentHandle {
  flyTo: (center: [number, number], zoom: number, duration?: number) => void;
  fitLocations: (locations: ILocation[], padding?: number) => void;
  fitToSearchResult: (
    searchPoint: [number, number],
    locations: ILocation[],
    padding?: number,
  ) => void;
  zoomOut: () => void;
  resize: () => void;
}

interface MapComponentProps {
  locations: ILocation[];
  accessToken: string;
  initialCenter?: [number, number];
  initialZoom?: number;
  selectedLocationId: string | null;
  onSelectLocation: (id: string | null) => void;
  onMapCenterChange?: (view: MapCenterChange) => void;
  onClusterSelect?: (cluster: LocationCluster) => void;
  showPopup?: boolean;
}

export const MapComponent = forwardRef<MapComponentHandle, MapComponentProps>(
  (
    {
      locations,
      accessToken,
      initialCenter = [-95.7129, 37.0902],
      initialZoom = 3.5,
      selectedLocationId,
      onSelectLocation,
      onMapCenterChange,
      onClusterSelect,
      showPopup = true,
    },
    ref,
  ) => {
    const mapRef = useRef<MapRef>(null);
    const [currentZoom, setCurrentZoom] = useState(initialZoom);

    const wideClusters = useMemo(
      () => buildLocationClusters(locations, 250),
      [locations],
    );
    const mediumClusters = useMemo(
      () => buildLocationClusters(locations, 80),
      [locations],
    );

    useImperativeHandle(ref, () => ({
      flyTo: (center, zoom, duration = 1000) => {
        mapRef.current?.flyTo({ center, zoom, duration });
      },
      fitLocations: (locs, padding = 80) => {
        const valid = locs.filter(
          (l) => l.fieldData.latitude != null && l.fieldData.longitude != null,
        );
        if (!valid.length) return;
        if (valid.length === 1) {
          mapRef.current?.flyTo({
            center: [valid[0].fieldData.longitude, valid[0].fieldData.latitude],
            zoom: 12,
            duration: 1000,
          });
          return;
        }
        const lngs = valid.map((l) => l.fieldData.longitude);
        const lats = valid.map((l) => l.fieldData.latitude);
        mapRef.current?.fitBounds(
          [
            [Math.min(...lngs), Math.min(...lats)],
            [Math.max(...lngs), Math.max(...lats)],
          ],
          { padding, duration: 1000, maxZoom: 12 },
        );
      },
      fitToSearchResult: (searchPoint, locs, padding = 80) => {
        const valid = locs.filter(
          (l) => l.fieldData.latitude != null && l.fieldData.longitude != null,
        );
        const allLngs = [
          searchPoint[0],
          ...valid.map((l) => l.fieldData.longitude),
        ];
        const allLats = [
          searchPoint[1],
          ...valid.map((l) => l.fieldData.latitude),
        ];
        mapRef.current?.fitBounds(
          [
            [Math.min(...allLngs), Math.min(...allLats)],
            [Math.max(...allLngs), Math.max(...allLats)],
          ],
          { padding, duration: 1000, maxZoom: 11 },
        );
      },
      zoomOut: () => {
        mapRef.current?.flyTo({
          center: [-95.7129, 37.0902],
          zoom: 3.5,
          duration: 1200,
        });
      },
      resize: () => {
        mapRef.current?.resize();
      },
    }));

    useEffect(() => {
      if (!selectedLocationId || !mapRef.current) return;
      const location = locations.find((l) => l.id === selectedLocationId);
      if (!location) return;
      mapRef.current.flyTo({
        center: [location.fieldData.longitude, location.fieldData.latitude],
        zoom: Math.max(currentZoom, 12),
        duration: 1000,
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedLocationId]);

    const showWideClusters = currentZoom < WIDE_CLUSTER_ZOOM;
    const showMediumClusters =
      currentZoom >= WIDE_CLUSTER_ZOOM && currentZoom <= MEDIUM_CLUSTER_ZOOM;
    const showIndividualMarkers = currentZoom >= MEDIUM_CLUSTER_ZOOM;

    const handleClusterClick = (
      cluster: LocationCluster,
      zoomInLevel: number,
    ) => {
      const map = mapRef.current?.getMap();

      const notifyClusterSelect = () => {
        onClusterSelect?.(cluster);
      };

      if (cluster.count === 1) {
        const location = cluster.locations[0];
        mapRef.current?.flyTo({
          center: [location.fieldData.longitude, location.fieldData.latitude],
          zoom: 12,
          duration: 800,
        });
        map?.once("moveend", notifyClusterSelect);
        return;
      }

      const lngs = cluster.locations.map((l) => l.fieldData.longitude);
      const lats = cluster.locations.map((l) => l.fieldData.latitude);
      mapRef.current?.fitBounds(
        [
          [Math.min(...lngs), Math.min(...lats)],
          [Math.max(...lngs), Math.max(...lats)],
        ],
        { padding: 100, duration: 800, maxZoom: zoomInLevel },
      );
      map?.once("moveend", notifyClusterSelect);
    };

    const selectedLocation =
      locations.find((l) => l.id === selectedLocationId) ?? null;

    const notifyUserCenterChange = (event: MapViewStateEvent) => {
      if (!isUserMapGesture(event)) return;
      const map = mapRef.current?.getMap();
      if (!map) return;
      const bounds = map.getBounds();
      if (!bounds) return;
      onMapCenterChange?.({
        lat: event.viewState.latitude,
        lng: event.viewState.longitude,
        zoom: event.viewState.zoom,
        bounds: {
          north: bounds.getNorth(),
          south: bounds.getSouth(),
          east: bounds.getEast(),
          west: bounds.getWest(),
        },
      });
    };

    return (
      <div className="w-full h-full overflow-hidden">
        <Map
          ref={mapRef}
          mapboxAccessToken={accessToken}
          initialViewState={{
            longitude: initialCenter[0],
            latitude: initialCenter[1],
            zoom: initialZoom,
          }}
          style={{ width: "100%", height: "100%" }}
          mapStyle="mapbox://styles/mapbox/streets-v12"
          onZoomEnd={(e) => {
            setCurrentZoom(e.viewState.zoom);
            notifyUserCenterChange(e);
          }}
          onDragEnd={notifyUserCenterChange}
          onLoad={(e) => setCurrentZoom(e.target.getZoom())}
        >
          <NavigationControl position="top-right" showCompass={false} />
          {showWideClusters &&
            wideClusters.map((cluster) => (
              <ClusterMarker
                key={cluster.id}
                cluster={cluster}
                size="lg"
                onZoomIn={handleClusterClick}
                zoomInLevel={5.5}
              />
            ))}

          {showMediumClusters &&
            mediumClusters.map((cluster) => (
              <ClusterMarker
                key={cluster.id}
                cluster={cluster}
                onZoomIn={handleClusterClick}
                zoomInLevel={MEDIUM_CLUSTER_ZOOM + 1}
              />
            ))}

          {showIndividualMarkers &&
            locations.map((location) => {
              if (
                location.fieldData.latitude == null ||
                location.fieldData.longitude == null
              )
                return null;
              return (
                <LocationMarker
                  key={location.id}
                  location={location}
                  isSelected={location.id === selectedLocationId}
                  onSelect={() => onSelectLocation(location.id)}
                />
              );
            })}

          {showPopup &&
            showIndividualMarkers &&
            selectedLocation?.fieldData.latitude &&
            selectedLocation?.fieldData.longitude && (
              <LocationPopup
                location={selectedLocation}
                onClose={() => onSelectLocation(null)}
              />
            )}
        </Map>
      </div>
    );
  },
);

MapComponent.displayName = "MapComponent";
